import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useMutation, useQuery } from '@tanstack/react-query';
import qs from 'qs';

import { ActionText } from '@components/action-text';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';

import { api } from 'api';
import { PasswordData, SubmitError } from 'api/users';
import { Loading } from 'components/Loading';
import { UnauthedPage } from 'components/unauthed-page';

import { SetPassword } from './set-password';
import { SetPasswordTwoFactor } from './SetPasswordTwoFactor';

type SetPasswordProps = {
  isReset?: boolean;
};

export const SetPasswordPage = ({ isReset = false }: SetPasswordProps) => {
  const { t } = useScopedTranslate(locale);
  const [state, setState] = useState({
    qrCodeUrl: '',
    disable: false,
  });
  const location = useLocation();
  const navigate = useNavigate();
  const parsed = qs.parse(location.search, { ignoreQueryPrefix: true });
  const uuid = parsed['uuid'] as string;
  const passwordUuid = parsed['password_uuid'] as string;

  const canSet = useQuery({
    queryKey: ['can-set-password'],
    queryFn: () => api.users.canSetPassword(uuid, passwordUuid),
    onSuccess: (res: PasswordData) => {
      setState((old) => ({ ...old, qrCodeUrl: res.qrCode || '' }));
    },
  });

  async function setPassword(data: { password1: string; password2: string }) {
    setState((old) => ({ ...old, disable: true }));
    const dataToSubmit = {
      password1: data.password1,
      password2: data.password2,
      passwordUuid,
      uuid,
    };
    submitPassword.mutate(dataToSubmit);
  }

  const submitPassword = useMutation(
    (data: { password1: string; password2: string; passwordUuid: string; uuid: string }) =>
      api.users.setPassword(data),
    {
      mutationKey: ['set-password'],
      onSuccess: () => {
        navigate(isReset ? '/resetpasswordsuccess' : '/setpasswordsuccess');
      },
      onError: (error: SubmitError) => {
        if (error.response) {
          setState({
            qrCodeUrl: state.qrCodeUrl,
            disable: false,
          });
        }
      },
    },
  );

  if (canSet.isLoading) {
    return (
      <UnauthedPage>
        <Loading />
      </UnauthedPage>
    );
  }

  if (canSet.data) {
    if (state.qrCodeUrl) {
      return (
        // @ts-ignore because of the missing props
        <SetPasswordTwoFactor qrCodeUrl={state.qrCodeUrl} uuid={uuid} passwordUuid={passwordUuid} />
      );
    }
    return <SetPassword onSubmit={setPassword} disableSubmit={state.disable} isReset={isReset} />;
  }
  return (
    <UnauthedPage>
      <Stack space={4}>
        <Stack space={2}>
          <Text center font="heading1" text={t('notFoundHeader')} />
          <Text center text={t('notFoundText')} />
        </Stack>
        <Text center>
          <ActionText.Link
            to="/signin"
            font="regular14"
            color="textHelper"
            children={t('goToSignin')}
          />
        </Text>
      </Stack>
    </UnauthedPage>
  );
};

const locale = {
  en: {
    notFoundHeader: 'Nothing here?',
    notFoundText: 'This link has either expired or your password has already been set',
    goToSignin: 'Go to sign in page',
  },
  is: {
    notFoundHeader: 'Ekkert hér?',
    notFoundText:
      'Þessi hlekkur er annað hvort útrunninn eða lykilorðið þitt hefur þegar verið vistað',
    goToSignin: 'Fara á innskráningarsíðu',
  },
};
